import { AxiosRequestConfig } from 'axios'
import {
  getEditPaymentParams,
  getHeaderParams,
  schedulePaymentParams
} from 'helpers/requestParams'
import apiEndpoints from 'const/endPoint'
import apiRequestor from 'services/apiRequestor'
import { userAccountInfo } from 'helpers/user'
import {
  GetPaymentsListResponse,
  GetProxPaymentsResponse,
  PaymentInfo,
  PaymentMethodsResponse
} from 'typings/payment'
import {
  AddPaymentAccountParams,
  AddPaymentAccountResponse,
  PaymentAccountProps
} from 'typings/paymentAccount'
import { DeletePaymentAccountProps } from 'redux/paymentAccount/typings'
import {
  AddBrcPaymentAccountRequest,
  AddProxPaymentAccountRequest
} from 'typings/apiRequest'

const paymentAPI = {
  getPaymentDetails: async (
    payload: PaymentInfo
  ): Promise<GetProxPaymentsResponse> => {
    const { accessToken, referenceId, accountId } = payload
    const { accountIdType } = userAccountInfo

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.payment}/${referenceId}`,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        accountIdType
      }
    }

    const response = await apiRequestor<GetProxPaymentsResponse>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  getPaymentsList: async (
    payload: PaymentInfo
  ): Promise<GetPaymentsListResponse> => {
    const { accessToken, accountId, currentPage, limit } = payload

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.payment,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        page: currentPage,
        size: limit
      }
    }

    const response = await apiRequestor<GetPaymentsListResponse>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  getOneTimePaymentList: async (
    payload: PaymentInfo
  ): Promise<GetPaymentsListResponse> => {
    const { accessToken, accountId, currentPage, limit, accountIdType } =
      payload

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.oneTimePayment,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        page: currentPage,
        size: limit,
        accountIdType
      }
    }

    const response = await apiRequestor<GetPaymentsListResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  getPaymentAccounts: async (
    payload: PaymentAccountProps
  ): Promise<PaymentMethodsResponse> => {
    const { isProx } = userAccountInfo
    const { accessToken, accountId, accountIdType } = payload

    const headerParams = getHeaderParams(accessToken, 'GET')

    const requestConfig: AxiosRequestConfig = {
      url: isProx ? apiEndpoints.paymentAccount : apiEndpoints.paymentMethod,
      method: 'GET',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        accountIdType,
        bankChannel: 'digital'
      }
    }

    const response = await apiRequestor<PaymentMethodsResponse>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  addProxPaymentAccount: async (
    payload: AddPaymentAccountParams
  ): Promise<AddPaymentAccountResponse> => {
    const {
      accessToken,
      accountId,
      accountNumber,
      routingNumber,
      nickname,
      userId
    } = payload

    const { accountIdType } = userAccountInfo
    const headerParams = getHeaderParams(accessToken, '', true)

    const requestBody: AddProxPaymentAccountRequest = {
      'cipher.accountId': accountId,
      accountIdType,
      paymentMethod: {
        bankAccountNumber: accountNumber,
        bankRoutingNumber: routingNumber,
        type: 'CHECKING',
        nickname,
        userId
      }
    }

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.paymentAccount,
      method: 'POST',
      headers: { ...headerParams },
      data: requestBody
    }

    const response = await apiRequestor<AddPaymentAccountResponse>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  addBrcPaymentAccount: async (
    params: AddPaymentAccountParams
  ): Promise<AddPaymentAccountResponse> => {
    const {
      accessToken,
      accountId,
      userId,
      accountNumber,
      routingNumber,
      nickname
    } = params

    const { accountIdType } = userAccountInfo

    const headerParams = getHeaderParams(accessToken, '')

    const requestBody: AddBrcPaymentAccountRequest = {
      'cipher.accountId': accountId,
      accountIdType,
      paymentMethod: {
        'cipher.bankAccountNumber': accountNumber,
        'cipher.bankRoutingNumber': routingNumber,
        type: 'CHECKING',
        nickname,
        userId
      }
    }

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.paymentMethod,
      method: 'POST',
      headers: { ...headerParams },
      data: requestBody
    }

    const response = await apiRequestor<AddPaymentAccountResponse>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  sendPayment: async (payload: PaymentInfo): Promise<PaymentInfo> => {
    const { accessToken, requestBody } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.payment,
      method: 'POST',
      headers: { ...headerParams },
      data: requestBody
    }

    const response = await apiRequestor<PaymentInfo>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  sendOneTimePayment: async (payload: PaymentInfo): Promise<PaymentInfo> => {
    const { accessToken, requestBody, paymentId } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.oneTimePayment,
      method: 'POST',
      headers: { ...headerParams },
      data: schedulePaymentParams(requestBody, paymentId)
    }

    const response = await apiRequestor<PaymentInfo>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  editPayment: async (payload: PaymentInfo): Promise<PaymentInfo> => {
    const { accessToken } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.payment,
      method: 'PUT',
      headers: { ...headerParams },
      data: getEditPaymentParams(payload)
    }

    const response = await apiRequestor<PaymentInfo>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  editOneTimePayment: async (payload: PaymentInfo): Promise<PaymentInfo> => {
    const { accessToken } = payload

    const headerParams = getHeaderParams(accessToken)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.oneTimePayment,
      method: 'PUT',
      headers: { ...headerParams },
      data: getEditPaymentParams(payload)
    }

    const response = await apiRequestor<PaymentInfo>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  cancelPayment: async (payload: PaymentInfo): Promise<unknown> => {
    const { accessToken, referenceId, accountId } = payload
    const { accountIdType } = userAccountInfo

    const headerParams = getHeaderParams(accessToken, 'DELETE')

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.payment}/${referenceId}`,
      method: 'DELETE',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        accountIdType
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  },
  cancelOneTimePayment: async (payload: PaymentInfo): Promise<unknown> => {
    const { accessToken, referenceId, accountId } = payload
    const { accountIdType } = userAccountInfo

    const headerParams = getHeaderParams(accessToken, 'DELETE')

    const requestConfig: AxiosRequestConfig = {
      url: `${apiEndpoints.oneTimePayment}/${referenceId}`,
      method: 'DELETE',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        accountIdType
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  deletePaymentAccount: async (
    payload: DeletePaymentAccountProps
  ): Promise<unknown> => {
    const { isProx } = userAccountInfo
    const { accessToken, id, accountId, accountIdType } = payload

    const headerParams = getHeaderParams(accessToken, 'DELETE')

    const url = isProx
      ? apiEndpoints.paymentAccount
      : apiEndpoints.paymentMethod

    const requestConfig: AxiosRequestConfig = {
      url: `${url}/${id}`,
      method: 'DELETE',
      headers: { ...headerParams },
      params: {
        'cipher.accountId': accountId,
        accountIdType
      }
    }

    const response = await apiRequestor<unknown>({
      requestConfig,
      moduleType: 'PAYMENT'
    })

    return response.data
  }
}

export default paymentAPI
